/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { Form, Modal } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ContentEditorHeader from "../contentEditorHeader";
import {
  selectItemId,
  selectStage,
  setIsLoading,
  setShowModal,
} from "../contentEditorSlice";
import "./addCard.scss";
import { useLocale } from "../../i18n/hooks";
import AddDownload from "./addDownload";
import { produce } from "immer";
import ContentEditorFooter from "../contentEditorFooter";
import { Locale } from "../../i18n/init";
import { Card, CardType, Resource, UserResource } from "../../resources/types";
import {
  addCard as addUserResourceCard,
  getUserResourceSubLessonCards,
  isValidUuid,
  saveCard as saveUserResourceCard,
} from "../../userResources/userResourceFunctions";
import {
  addCard as addResourceCard,
  saveCard as saveResourceCard,
} from "../../resources/ResourceFunctions";
import { useTranslation } from "react-i18next";
import AddTab from "./addTab";
import AddContent from "./addContent";
import AddType from "./addType";
import { activityCardTypes, staticCardTypes } from "../utils";
import { getResourceCode } from "../../../utils/GetResourceCode";
import {
  SCROLL_TO_QUERY_PARAM_ID,
  setScrollTo,
} from "../../activityCards/CardSlice";

type Props = {
  resource: UserResource;
  dev?: boolean;
};

const AddCard: React.FC<Props> = ({ resource, dev = false }) => {
  const step = useAppSelector(selectStage());

  // Step 1: Card Type Edit
  // Step 2: Card Content Edit
  // Step 3: Card Hint
  // Step 4: Card Extension Edit
  // Step 5: Card Answer Edit
  // Step 6: Card Download Edit

  const itemId = useAppSelector(selectItemId());
  const locale: Locale = useLocale();
  const { t: _ } = useTranslation();
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();

  let { lessonId, pageId } = useParams();

  const lesson = resource?.Object.lessons[Number(lessonId)];
  const pageIdNumber = Number(pageId);
  const subLessonUuid = lesson?.subLessons[pageIdNumber]?.uuid ?? "";

  const [card, setCard] = useState<Card>();
  const [initCard, setInitCard] = useState<Card>();
  const isNewCard = !!itemId ? !isValidUuid(itemId) : false;

  const isStaff = useAppSelector((state) => state.auth.isStaff);

  const getCard = useCallback(async () => {
    const cards = await getUserResourceSubLessonCards({
      resource: resource.Object,
      code: getResourceCode(resource),
      locale,
      lessonIndex: Number(lessonId),
      subLessonUuid,
      copy: resource.Copy,
    });
    const card: Card | undefined = cards?.find((item) => item.UUID === itemId);
    setCard(card);
    setInitCard(card);
  }, [lessonId, locale, resource, itemId, subLessonUuid]);
  const [type, setType] = useState<CardType>();
  //const [content, setContent] = useState<string>("");
  //const [title, setTitle] = useState<string>("");
  //const [hint, setHint] = useState<string>("");
  //const [extension, setExtension] = useState<string>("");
  //const [answer, setAnswer] = useState<string>("");
  //const [downloads, setDownloads] = useState<Array<Download>>([]);
  const [validated, setValidated] = useState(false);

  const updateCard = useCallback((value: Card) => {
    setCard(value);
  }, []);

  useEffect(() => {
    if (card) {
      setType(
        produce(card.Object.type, (draft) => {
          switch (draft) {
            case "questionAndAnswer":
            case "randomGenerator":
              draft = "answerQuestion";
              break;
          }

          return draft;
        }),
      );
    }
  }, [card, updateCard]);

  useEffect(() => {
    getCard();
  }, [getCard]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === true) {
      let uuid = undefined;
      dispatch(setIsLoading(true));
      if (!dev) {
        if (card) {
          let _data;

          if (type !== card.Object.type && type) {
            const data = await import(
              `../../activityCards/cards/${
                !["gallery", "carousel"].includes(type) &&
                staticCardTypes.find((_type) => type === _type.id)
                  ? "Static"
                  : type.charAt(0).toUpperCase() + type.slice(1)
              }Card/template.json`
            )
              .then((json) => {
                return json.data;
              })
              .catch(() => {
                throw Error("Unable to retrieve correct data");
              });

            _data = {
              ...data,
              title: card.Object.data.title,
              content: card.Object.data.content,
            };
          }

          const _card = {
            ...card,
            Object: {
              ...card.Object,
              type: type ?? "read",
              data: _data ?? card.Object.data,
            },
          };

          if (resource.PK.includes("USER_BL#")) {
            uuid = await saveUserResourceCard(
              _card,
              locale,
              resource,
              Number(lessonId),
              pageIdNumber,
            );
          } else {
            if (isStaff) {
              uuid = await saveResourceCard(
                _card,
                resource as Resource,
                Number(lessonId),
                pageIdNumber,
              );
            }
          }
        } else {
          if (isNewCard && type && !!itemId) {
            if (resource.PK.includes("USER_BL#")) {
              uuid = await addUserResourceCard(
                type,
                locale,
                resource,
                Number(lessonId),
                pageIdNumber,
                Number(itemId),
              );
            } else {
              if (isStaff) {
                uuid = await addResourceCard(
                  type,
                  resource as Resource,
                  Number(lessonId),
                  pageIdNumber,
                  Number(itemId),
                );
              }
            }
          }
        }
      }

      if (uuid !== undefined) {
        const params = new URLSearchParams();
        params.set(SCROLL_TO_QUERY_PARAM_ID, uuid);
        dispatch(setScrollTo(uuid)); // save to state here as well as app in case app does not rerender.
        setSearchParams(params);
      }

      dispatch(setShowModal(false));
    }

    setValidated(true);
  };

  const modalClassVariant = () => {
    switch (step) {
      case 3:
        return "hint";
      case 4:
        return "extension";
      case 5:
        return "answer";
      case 6:
        return "download";
      default:
        return type;
    }
  };

  const modalTypeVariant = () => {
    switch (step) {
      case 3:
      case 4:
      case 5:
      case 6:
        return "tab";
      default:
        return "card";
    }
  };

  const modalHeaderTitleVariant = () => {
    const cardTypeMatch = staticCardTypes
      .concat(activityCardTypes)
      .find((item) => item.id === type);

    switch (step) {
      case 3:
        return "hint";
      case 4:
        return "extension";
      case 5:
        return "answer";
      case 6:
        return "download";
      default:
        return cardTypeMatch?.name;
    }
  };

  const linkLocationId = () => {
    switch (step) {
      case 1:
        return "cards";
      case 2:
        return "basic-content-cards";
      case 3:
      case 4:
      case 5:
      case 6:
        return "additional-content-tabs";
    }
  };

  return (
    <>
      <ContentEditorHeader
        modalHeaderClass={step === 1 ? "" : modalClassVariant()}
        closeModalVariant={step === 1 ? undefined : "white"}
        modalType={modalTypeVariant()}
        modalHeaderTitle={modalHeaderTitleVariant()}
        locationId={linkLocationId()}
      />
      <Modal.Body className="p-0 content-editor-body">
        <Form
          role="form"
          onSubmit={handleSubmit}
          validated={validated}
          noValidate
          className={`card-modal-content unselectable`}
        >
          <div>
            {step === 1 && (
              <AddType
                type={type}
                updateType={(value: CardType) => setType(value)}
              />
            )}
            {card && (
              <>
                {step === 2 && initCard && (
                  <AddContent
                    updateCard={updateCard}
                    card={card}
                    initCard={initCard}
                  />
                )}
                {step === 3 && initCard && (
                  <AddTab
                    updateCard={updateCard}
                    card={card}
                    initCard={initCard}
                    tabType={"hint"}
                  />
                )}
                {step === 4 && initCard && (
                  <AddTab
                    updateCard={updateCard}
                    card={card}
                    initCard={initCard}
                    tabType={"extension"}
                  />
                )}
                {step === 5 && initCard && (
                  <AddTab
                    updateCard={updateCard}
                    card={card}
                    initCard={initCard}
                    tabType={"answer"}
                  />
                )}
                {step === 6 && card && (
                  <>
                    <AddDownload
                      card={card}
                      updateCard={updateCard}
                      validated={validated}
                    />
                  </>
                )}
              </>
            )}
          </div>
          <ContentEditorFooter
            warningMsg={
              card?.Object.type && type !== card?.Object.type
                ? _("Change in card type may result in loss of data.")
                : undefined
            }
          />
        </Form>
      </Modal.Body>
    </>
  );
};

export default AddCard;
